import { isOnOrigin } from '@app/shared'
import { PLATFORM_NAME, productName } from '@app/shared/brand'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { establishPlatformSession } from '../lib/auth'
import { currentSite } from '../lib/public-router'
import { useTRPC } from '../lib/trpc'
import { Button, Field, Notice, TextInput } from '../ui'
import { Logo } from '../ui/Logo'
import './login.css'

/**
 * The sign-in screen. Two ways in, one identity.
 *
 * A password is offered first because somebody who has set one is here to type
 * it, and a link is a round trip through an inbox. Anybody without one — which
 * includes everybody invited, since a link is how an account starts — takes the
 * other path, so it stays a peer rather than a fallback.
 *
 * Invite-only, and the responses are deliberately uninformative in both
 * directions: the link answers the same whether or not an account exists, and
 * the password answers the same sentence for every kind of failure. The screen
 * never reveals who has an account.
 */
export function LoginScreen({
  onBack,
  backTo,
  notice,
  next,
}: {
  onBack?: () => void
  /**
   * What going back returns to, named.
   *
   * The caller knows, because it decided what to draw behind this — a shul's
   * zmanim, the directory, one of our front doors. A label written here could
   * only guess, and guessed wrong on every site that was not the first one.
   */
  backTo?: string
  /** Why the person is looking at this rather than at the app. */
  notice?: string | null
  /** The path this sign-in was opened to reach. */
  next?: string
}) {
  const trpc = useTRPC()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [byLink, setByLink] = useState(false)
  const request = useMutation(trpc.auth.requestLink.mutationOptions())
  const handoff = useMutation(trpc.auth.handoff.mutationOptions())
  const signIn = useMutation(
    trpc.auth.signInWithPassword.mutationOptions({
      /*
       * Signed in here; now tell the platform, so every other site of ours can
       * find this session without a second sign-in. That leaves the page, which
       * is the reload this needed anyway — the whole app hangs off `auth.me`,
       * so letting it reload is simpler and more correct than teaching every
       * query about a new session. Where the trip does not happen, the plain
       * reload does.
       */
      onSuccess: () =>
        establishPlatformSession(handoff, () =>
          window.location.assign(
            isOnOrigin(next, window.location.origin) ? next : '/',
          ),
        ),
    }),
  )

  /*
   * Whose sign-in screen this is, from the address — the same question the site
   * chrome asks, answered the same way. One screen served on three hosts, and
   * the name on it is the one the person came to.
   */
  const site = currentSite()
  const product = site === 'platform' || site === 'org' ? undefined : site
  const name = product ? productName(product) : PLATFORM_NAME

  return (
    <div className="login">
      <div className="login__card">
        <h1 className="login__brand">
          <Logo size={28} product={product ?? 'platform'} />
          <span>{name}</span>
        </h1>
        {onBack && backTo ? (
          <button type="button" className="login__back" onClick={onBack}>
            ← Back to {backTo}
          </button>
        ) : null}
        {notice ? <Notice tone="error">{notice}</Notice> : null}
        {request.isSuccess ? (
          <Notice>
            If <strong>{email}</strong> has an account, a sign-in link is on its
            way. It is good for 15 minutes.
          </Notice>
        ) : (
          <>
            <p className="login__lede">
              {byLink
                ? "Enter your email and we'll send you a sign-in link."
                : 'Sign in with your email and password.'}
            </p>
            <form
              className="login__form"
              onSubmit={(e) => {
                e.preventDefault()
                const at = email.trim()
                if (!at) return
                if (byLink) request.mutate({ email: at, next })
                else if (password) signIn.mutate({ email: at, password })
              }}
            >
              <Field label="Email">
                <TextInput
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoFocus
                />
              </Field>
              {byLink ? null : (
                <Field label="Password">
                  <TextInput
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                </Field>
              )}
              <Button
                type="submit"
                disabled={
                  request.isPending ||
                  signIn.isPending ||
                  !email.trim() ||
                  (!byLink && !password)
                }
              >
                {byLink
                  ? request.isPending
                    ? 'Sending…'
                    : 'Send sign-in link'
                  : signIn.isPending
                    ? 'Signing in…'
                    : 'Sign in'}
              </Button>
            </form>
            {/*
             * The other door, and the answer to forgetting a password — there
             * is no separate reset, because signing in with a link and setting
             * a new one is the same act with one fewer mechanism to get right.
             */}
            <button
              type="button"
              className="login__alt"
              onClick={() => setByLink((v) => !v)}
            >
              {byLink
                ? 'Sign in with a password instead'
                : 'Forgot your password? Email me a sign-in link'}
            </button>
            {request.error && (
              <Notice tone="error">{request.error.message}</Notice>
            )}
            {signIn.error && (
              <Notice tone="error">{signIn.error.message}</Notice>
            )}
          </>
        )}
      </div>
    </div>
  )
}
